import React, { useEffect, useState, useRef } from 'react';
import { User } from 'lucide-react';
import { getCustomBlockItemsByStatus } from '../../../../services/customBlockDataService';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'large',
  defaultAlignment: 'center',
  defaultPadding: 'spacious',
};

const TITLE_COLORS = {
  GM: '#d97706', IM: '#9333ea', FM: '#2563eb',
  WGM: '#d97706', WIM: '#9333ea', WFM: '#2563eb',
  CM: '#059669', NM: '#059669',
};

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

/* Person-first card: photo + name on top, quote below */
const EndorsementCard = ({ item, index, styles }) => {
  const [ref, visible] = useInView();
  const [hovered, setHovered] = useState(false);
  const titleColor = TITLE_COLORS[item.title] || styles.accentColor;

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `all 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.1}s`,
      }}
    >
      <div
        className="relative h-full rounded-2xl overflow-hidden transition-all duration-400 text-center"
        style={{
          background: `linear-gradient(135deg, ${styles.cardBackground}, ${styles.cardBackground}ee)`,
          border: `1px solid ${hovered ? `${styles.accentColor}30` : `${styles.borderColor}25`}`,
          boxShadow: hovered
            ? `0 20px 40px rgba(0,0,0,0.25)`
            : `0 4px 16px rgba(0,0,0,0.1)`,
          transform: hovered ? 'translateY(-4px)' : 'none',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Accent top line */}
        <div
          className="h-px transition-all duration-400"
          style={{
            background: hovered
              ? `linear-gradient(90deg, transparent 10%, ${styles.accentColor}80 50%, transparent 90%)`
              : `linear-gradient(90deg, transparent 20%, ${styles.accentColor}20 50%, transparent 80%)`,
          }}
        />

        <div className="px-6 py-8 md:px-8 md:py-10">
          {/* Avatar — centered, prominent */}
          <div className="flex justify-center mb-5">
            {item.photoUrl ? (
              <img
                src={item.photoUrl}
                alt={item.name}
                className="w-16 h-16 rounded-full object-cover"
                style={{
                  boxShadow: `0 0 0 3px ${styles.cardBackground}, 0 0 0 4px ${styles.accentColor}25, 0 8px 24px rgba(0,0,0,0.2)`,
                }}
              />
            ) : (
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${styles.accentColor}15, ${styles.accentColor}08)`,
                  boxShadow: `0 0 0 3px ${styles.cardBackground}, 0 0 0 4px ${styles.accentColor}15`,
                }}
              >
                <User size={22} style={{ color: styles.accentColor }} className="opacity-40" />
              </div>
            )}
          </div>

          {/* Name */}
          <h3
            className="text-base font-bold mb-1"
            style={{ color: styles.headingColor }}
          >
            {item.name}
          </h3>

          {/* Title + Federation */}
          <p className="text-xs mb-5" style={{ color: styles.mutedColor }}>
            {item.title !== 'None' && (
              <span style={{ color: titleColor }} className="font-bold">{item.title}</span>
            )}
            {item.title !== 'None' && item.federation && ' \u00b7 '}
            {item.federation}
          </p>

          {/* Divider */}
          <div
            className="w-8 h-px mx-auto mb-5"
            style={{ backgroundColor: `${styles.accentColor}20` }}
          />

          {/* Quote */}
          <p
            className="text-sm leading-[1.8] italic"
            style={{ color: `${styles.bodyColor}cc` }}
          >
            &ldquo;{item.quote}&rdquo;
          </p>
        </div>
      </div>
    </div>
  );
};

const EndorsementWallRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const { heading } = content;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  useEffect(() => {
    const load = async () => {
      if (!profileId) { setLoading(false); return; }
      try {
        const fetched = await getCustomBlockItemsByStatus(profileId, 'endorsement-wall', 'approved');
        setItems(fetched);
      } catch (error) {
        console.error('Error loading endorsements:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [profileId]);

  if (loading) {
    return (
      <div className={`w-full ${styles.sectionPaddingClass}`} style={{ backgroundColor: styles.sectionBackground }}>
        <div className="max-w-5xl mx-auto flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: styles.accentColor, borderTopColor: 'transparent' }} />
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={`w-full ${styles.sectionPaddingClass}`} style={{ backgroundColor: styles.sectionBackground }}>
        <div className="max-w-5xl mx-auto text-center py-16">
          {heading && (
            <h2
              className={`${styles.headingSizeClass} font-bold mb-6`}
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h2>
          )}
          <p className="text-sm" style={{ color: styles.mutedColor }}>Endorsements coming soon.</p>
        </div>
      </div>
    );
  }

  const getGridClass = () => {
    if (items.length === 1) return 'grid-cols-1 max-w-md mx-auto';
    if (items.length === 2) return 'grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto';
    if (items.length <= 4) return 'grid-cols-1 sm:grid-cols-2 max-w-4xl mx-auto';
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto';
  };

  return (
    <div className={`w-full ${styles.sectionPaddingClass}`} style={{ backgroundColor: styles.sectionBackground }}>
      <div className="max-w-6xl mx-auto">
        {heading && (
          <h2
            className={`${styles.headingSizeClass} font-bold text-center mb-12 md:mb-16`}
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            {heading}
          </h2>
        )}

        <div className={`grid gap-6 ${getGridClass()}`}>
          {items.map((item, idx) => (
            <EndorsementCard key={item.id} item={item} index={idx} styles={styles} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EndorsementWallRenderer;
